import React, { useEffect, useState } from "react";

import {
  Box,
  Flex,
  Stack,
  Text,
  SegmentedControl,
  AlertMessage,
} from "flicket-ui";
import { FormikHelpers } from "formik";

import { Footer, Layout, Menu, Seo } from "~components";
import { useAccount, useIsMobile, useOrganization } from "~hooks";
import { sdk } from "~lib";

import {
  AuthContainer,
  ContentContainer,
  CreateAccountLink,
  LoginForm,
  MobileLogo,
} from "~components/auth";
import { LoginValues } from "~form/login";
import { RequestMagicLinkInput } from "~graphql/sdk";
import { getError, handlePromise } from "~lib/helpers";
import { useRouter } from "next/router";

const handleMagicLink = async (orgId: string, input: RequestMagicLinkInput) =>
  handlePromise(async () => sdk({ orgId }).requestMagicLink({ input }));

const Login = () => {
  const router = useRouter();
  const isMobile = useIsMobile();

  const { login } = useAccount();
  const { organization } = useOrganization();
  const [magicLinkState, setMagicLinkState] = useState<{
    sent: boolean;
    error?: string;
  }>({ sent: false });

  const formType = router.query.type === "password" ? "password" : "link";

  useEffect(() => {
    if (!router.query.type) {
      void router.replace("/login?type=link");
    }
  }, [router]);

  const handleSubmit = async (
    values: LoginValues[typeof formType],
    actions: FormikHelpers<LoginValues[typeof formType]>
  ) => {
    if (formType === "link") {
      const magicLinkInput: RequestMagicLinkInput = {
        ...values,
        redirectUrl: decodeURIComponent(
          (router.query.redirect as string) ?? ""
        ),
      };

      const { data, error } = await handleMagicLink(
        organization?.id,
        magicLinkInput
      );

      if (error) {
        setMagicLinkState({ sent: false, error: getError(error, "graphQL") });
      }

      if (data) {
        setMagicLinkState({ sent: true });
      }

      actions.setSubmitting(false);
      return;
    }

    const success = await login(values as LoginValues["password"]);

    if (!success) {
      actions.setSubmitting(false);
    }
  };

  const onSubmit = (
    values: LoginValues[typeof formType],
    actions: FormikHelpers<LoginValues[typeof formType]>
  ) => {
    handleSubmit(values, actions).catch(console.error);
  };

  return (
    <>
      <Seo
        title="Login"
        description={`Sign in to your ${organization?.name} account.`}
      />
      <Menu />
      <Layout layoutType="auth">
        <ContentContainer>
          <MobileLogo logo={organization?.branding?.logo} />
          <Flex
            bg="N100"
            justifyContent="flex-start"
            flex={1}
            alignItems="center"
            flexDir="column"
          >
            <AuthContainer>
              <Box w="100%">
                <Text variant="header.M" textAlign="center" mb={3}>
                  Log in
                </Text>
                <Stack flexDir="column" alignItems="flex-start" gap={5} mb={3}>
                  <SegmentedControl
                    onChange={(value) => {
                      void router.push(`/login?type=${value}`);
                    }}
                    data={[
                      { value: "link", label: "Send login link" },
                      { value: "password", label: "Password login" },
                    ]}
                    value={formType}
                    width="100%"
                  />
                </Stack>
                <Stack my={2} width={"100%"}>
                  {magicLinkState.sent && (
                    <AlertMessage
                      description={
                        <>
                          If an account exists with this email address,
                          you&apos;ll receive a login link shortly. Please check
                          your inbox and spam folder.
                        </>
                      }
                      title="Login link sent"
                      type="success"
                      size="md"
                      onDismiss={() =>
                        setMagicLinkState({ sent: false, error: undefined })
                      }
                      width={"100%"}
                    />
                  )}
                  {magicLinkState.error && (
                    <AlertMessage
                      // Generic message to avoid leaking info about account existence.
                      // This method should always just return true anyway.
                      description={<>Please try again</>}
                      title="Error sending login link"
                      type="error"
                      size="md"
                      onDismiss={() =>
                        setMagicLinkState({ sent: false, error: undefined })
                      }
                      width={"100%"}
                    />
                  )}
                </Stack>
                <LoginForm formType={formType} handleSubmit={onSubmit} />
              </Box>
            </AuthContainer>
            <Flex
              justifyContent="center"
              alignItems="center"
              mt={2}
              mb={{ _: 3, md: "28px" as any }}
            >
              <CreateAccountLink />
            </Flex>
          </Flex>
          {!isMobile && <Footer />}
        </ContentContainer>
      </Layout>
    </>
  );
};

export default Login;
